import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { setShowLoginModal } from "../../redux/reducer/modalReducer";
import { cldResize } from "../../utils";
import { accountLinks } from "./AccountMenu";

/** Menu trượt từ trái trên mobile: tài khoản + danh mục + liên kết nhanh. */
const MobileDrawer = ({ open, onClose, categories, onLogout }) => {
  const dispatch = useDispatch();
  const { currentUser } = useSelector((state) => state.authReducer?.login);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  return (
    <div className={`fixed inset-0 z-[900] lg:hidden ${open ? "visible" : "invisible"}`} aria-hidden={!open}>
      <div
        className={`absolute inset-0 bg-slate-900/50 transition-opacity duration-200 ${open ? "opacity-100" : "opacity-0"}`}
        onClick={onClose}
      />
      <nav
        aria-label="Menu chính"
        className={`absolute inset-y-0 left-0 flex w-[86%] max-w-[340px] flex-col bg-white shadow-pop transition-transform duration-200 ease-out ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Đầu menu: tài khoản */}
        <div className="flex items-center gap-3 bg-brand-600 px-4 py-4 text-white">
          <i className="bi bi-person-circle text-4xl leading-none" aria-hidden="true" />
          <div className="min-w-0 flex-1">
            {currentUser ? (
              <>
                <p className="m-0 truncate text-lg font-semibold">{currentUser?.fullname || currentUser?.username}</p>
                <p className="m-0 truncate text-sm text-white/80">{currentUser?.email}</p>
              </>
            ) : (
              <button
                type="button"
                onClick={() => {
                  onClose();
                  dispatch(setShowLoginModal(true));
                }}
                className="text-left text-white"
              >
                <span className="block text-lg font-semibold">Đăng nhập / Đăng ký</span>
                <span className="block text-sm text-white/80">Theo dõi đơn hàng, nhận ưu đãi riêng</span>
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full hover:bg-white/15"
            aria-label="Đóng menu"
          >
            <i className="bi bi-x-lg text-lg" aria-hidden="true" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {currentUser && (
            <ul className="m-0 grid list-none grid-cols-3 gap-2 border-b border-slate-100 p-3">
              {accountLinks(currentUser).map((l) => (
                <li key={l.to} className="list-none">
                  <Link
                    to={l.to}
                    target={l.external ? "_blank" : undefined}
                    onClick={onClose}
                    className="flex flex-col items-center gap-1 rounded-xl bg-slate-50 px-1 py-2.5 text-center text-sm font-medium text-slate-700 no-underline"
                  >
                    <i className={`bi ${l.icon} text-xl text-brand-600`} aria-hidden="true" />
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          )}

          <p className="m-0 px-4 pb-1 pt-4 text-sm font-semibold uppercase tracking-wide text-slate-400">Danh mục sản phẩm</p>
          <ul className="m-0 list-none p-0 px-2">
            {(categories ?? []).map((c) => (
              <li key={c?._id || c?.slug} className="list-none">
                <Link
                  to={`/danh-muc/${c?.slug}`}
                  onClick={onClose}
                  className="flex items-center gap-3 rounded-lg px-2 py-2.5 text-lg text-slate-800 no-underline active:bg-slate-50"
                >
                  <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-slate-50">
                    <img src={cldResize(c?.image, 96)} alt="" width={24} height={24} className="h-6 w-6 object-contain" loading="lazy" />
                  </span>
                  <span className="flex-1">{c?.name}</span>
                  <i className="bi bi-chevron-right text-sm text-slate-300" aria-hidden="true" />
                </Link>
              </li>
            ))}
          </ul>

          <p className="m-0 px-4 pb-1 pt-4 text-sm font-semibold uppercase tracking-wide text-slate-400">Khám phá</p>
          <ul className="m-0 list-none p-0 px-2 pb-4">
            {[
              { to: "/flash-sale", icon: "bi-lightning-charge", label: "Flash Sale" },
              { to: "/bai-viet", icon: "bi-journal-medical", label: "Góc sức khỏe" },
              { to: "/tac-gia", icon: "bi-person-badge", label: "Đội ngũ chuyên gia" },
              { to: "/cau-hoi-thuong-gap", icon: "bi-question-circle", label: "Câu hỏi thường gặp" },
              { to: "/lien-he", icon: "bi-headset", label: "Liên hệ" },
            ].map((l) => (
              <li key={l.to} className="list-none">
                <Link to={l.to} onClick={onClose} className="flex items-center gap-3 rounded-lg px-2 py-2.5 text-lg text-slate-800 no-underline active:bg-slate-50">
                  <i className={`bi ${l.icon} w-9 text-center text-xl text-brand-600`} aria-hidden="true" />
                  {l.label}
                </Link>
              </li>
            ))}
            {currentUser && (
              <li className="list-none">
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    onLogout();
                  }}
                  className="flex w-full items-center gap-3 rounded-lg px-2 py-2.5 text-left text-lg text-slate-800"
                >
                  <i className="bi bi-box-arrow-right w-9 text-center text-xl text-slate-400" aria-hidden="true" />
                  Đăng xuất
                </button>
              </li>
            )}
          </ul>
        </div>

        <a href="tel:+84967711633" className="flex items-center gap-3 border-t border-slate-100 px-4 py-3 text-slate-800 no-underline">
          <i className="bi bi-telephone-fill text-xl text-brand-600" aria-hidden="true" />
          <span className="leading-tight">
            <span className="block text-sm text-slate-500">Tư vấn miễn phí (8h–18h)</span>
            <span className="block text-lg font-semibold">0967.711.633</span>
          </span>
        </a>
      </nav>
    </div>
  );
};

export default MobileDrawer;
